import { buildQuery, IFilter, IPage } from "@/api/api.ts";
import { API_URL } from "@/config";
import axios from "axios";
import { SalesInfo, UserType } from "./profile";
import { IStoreUser } from "./users";
import { IInvoice, IInvoiceItem } from "./invoice";

const API = `${API_URL}/store`;

export interface IOrderImage {
    id: number;
    path: string;
    created_at: string;
    uploaded_by_id: number;
}

export interface IAddress {
    address1: string;
    address2: string;
    city: string;
    state: string;
    zip_code: string;
}

interface IStoreAddress extends IAddress {
    id: number;
    store_id: number;
}

export interface IHours {
    day: number;
    open_time?: Date | string;
    close_time?: Date | string;
    open: boolean;
}

export interface IStoreHours extends IHours {
    id: number;
    store_id: number;
}

export enum ItemSize {
    SMALL = "<48x36x12",
    MEDIUM = "48x36x12 - 72x48x18",
    LARGE = "72x48x18 - 84x60x24",
    EXTRA_LARGE = ">84x60x24",
}

export enum DeliveryWindow {
    DEFAULT = "7 Days",
    FIVE_DAYS = "5 Days",
    THREE_DAYS = "3 Days",
    TWO_DAYS = "2 Days",
}

export interface IStoreOrder {
    id?: number;
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
    referral?: string;
    order_location?: string;
    delivery_address: IAddress;
    item_length: number;
    item_width: number;
    item_height: number;
    item_size: string;
    delivery_window: string;
    item_description: string;
    estimated_value: string;
    item_quantity: number;
    floor_level: number;
    required_to_be_present: boolean;
    elevator_available: boolean;
    inside_or_outside: boolean;
    assembly_required: boolean;
    max_weight: boolean;
    needs_wrapped: boolean;
    store_id: number;
    invoice_file?: string;
    invoice_uploaded_at?: string;
    dropoff_instructions: string;
    shipping_instructions: string;
    driver_bonus?: string;
    payment_intent_id?: string;
    payment_intent_paid?: boolean;
    images?: IOrderImage[];
    invoice_item_id?: string;
}

export interface IMetaDataResponse {
    order_id?: number;
    finished?: boolean;
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
    order_location: string;
    delivery_address_address1: string;
    delivery_address_address2: string;
    delivery_address_city: string;
    delivery_address_state: string;
    delivery_address_zip_code: string;
    item_description: string;
    estimated_value: string;
    item_quantity: number;
    assembly_cost: string;
    base_price: string;
    delivery_window_cost: string;
    distance_cost: string;
    floor_level_cost: string;
    max_weight_cost: string;
    max_weight: boolean;
    needs_wrapped: boolean;
    distance: string;
    distance_multiplier: string;
    distance_surcharge: string;
    delivery_window: DeliveryWindow;
    item_size: ItemSize;
    floor_level: number;
    elevator_available: boolean;
    assembly_required: boolean;
    dropoff_instructions: string;
    shipping_instructions: string;
    referral?: string;
    discount?: string;
    high_value_cost: string;
    required_to_be_present?: boolean;
    inside_or_outside?: boolean;
    location_cost: string;
}

export interface IPaymentIntent {
    id: string;
    amount: number;
    status: string;
    client_secret: string;
    metadata: IMetaDataResponse;
}
export interface IUpdateOrder {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone_number?: string;
    referral?: string;
    delivery_address?: IAddress;
    item_description?: string;
    estimated_value?: string;
    item_quantity?: number;
    item_length?: number;
    item_width?: number;
    item_height?: number;
    item_size?: string;
    delivery_window?: string;
    floor_level?: number;
    required_to_be_present?: boolean;
    inside_or_outside?: boolean;
    elevator_available?: boolean;
    assembly_required?: boolean;
    max_weight?: boolean;
    needs_wrapped?: boolean;
    dropoff_instructions?: string;
    shipping_instructions?: string;
    driver_bonus?: string;
    payment_intent_paid?: boolean;
    // Making this so the handleFinish on InvoiceCheckoutForm just updates this flag to true
    invoice_finished?: boolean;
}

export interface DBStoreOrder extends IStoreOrder {
    id: number;
    created_at: string;
    accepted: boolean;
    accepted_time: string;
    picked_up: boolean;
    picked_up_time: string;
    in_route: boolean;
    in_route_time: string;
    expected_pickup_time: string;
    delivered: boolean;
    delivered_time: string;
    paid_driver: boolean;
    payment_intent_id?: string;
    referral?: string;
    driver: UserType;
    store?: IStore;
}

export interface IStore {
    id: number;
    name: string;
    primary_email: string;
    is_using_invoice: boolean;
    store_address: IStoreAddress;
    orders: DBStoreOrder[];
    store_hours: IStoreHours[];
    salesperson_id: number | undefined;
}

export interface ICreateStore {
    name: string;
    primary_email: string;
    store_address: IAddress;
    is_using_invoice: boolean;
    salesperson_id: number | undefined;
}

export interface IUpdateStore extends ICreateStore {}

export interface IStoreAccount extends UserType {
    store_id: number;
}

export interface ICreateStoreOrder extends IStoreOrder {
    payment_intent_id: string;
}

export const createStore = async (data: ICreateStore) => {
    const result: { data: IStore } = await axios.post(`${API}`, data);
    return result.data;
};

export const deleteStore = async (store_id: number) => {
    const result: { data: IStore } = await axios.delete(`${API}/${store_id}`);
    return result.data;
};

export const getAllStores = async (
    page: number = 1,
    size: number = 50,
    filter?: IFilter
) => {
    const result = await axios.get<IPage<IStore>>(
        `${API}?page=${page}&size=${size}&${buildQuery(filter)}`,
        {
            headers: {
                "Cache-Control": "no-cache, no-store, must-revalidate",
                Pragma: "no-cache",
                Expires: "0",
            },
        }
    );
    return result.data;
};

export const addStoreAccount = async (store_id: number, user_id: number) => {
    const result: { data: IStoreAccount } = await axios.post(
        `${API}/${store_id}/accounts?user_id=${user_id}`
    );
    return result.data;
};

export const getStore = async (store_id: string | number) => {
    const result: { data: IStore } = await axios.get(`${API}/${store_id}`);
    return result.data;
};

export const getStoreAccounts = async (
    store_id: number,
    page: number = 1,
    size: number = 50,
    filter: IFilter
) => {
    const result = await axios.get<IPage<IStoreAccount>>(
        `${API}/${store_id}/accounts?page=${page}&size=${size}&${buildQuery(filter)}`
    );
    return result.data;
};

export const getSalesRepUsers = async (
    rep_id: number,
    page: number = 1,
    size: number = 50,
    filter: IFilter
) => {
    const result = await axios.get<IPage<IStoreUser>>(
        `${API}/${rep_id}/accounts/users?page=${page}&size=${size}&${buildQuery(filter)}`
    );
    return result.data;
};

export const removeStoreAccount = async (store_id: number, user_id: number) => {
    const result: { data: IStoreAccount } = await axios.delete(
        `${API}/${store_id}/accounts?user_id=${user_id}`
    );
    return result.data;
};

export const getStoreOrders = async (
    store_id: number,
    page: number = 1,
    size: number = 50,
    filter: IFilter
) => {
    const result = await axios.get<IPage<DBStoreOrder>>(
        `${API}/${store_id}/orders?page=${page}&size=${size}&${buildQuery(
            filter
        )}`
    );
    return result.data;
};

export const getSalesPerson = async (store_id: number) => {
    const result: { data: SalesInfo } = await axios.get(
        `${API}/${store_id}/salesperson`
    );
    return result.data;
};

export const updateStore = async (store_id: number, data: IUpdateStore) => {
    const result: { data: IStore } = await axios.put(
        `${API}/${store_id}`,
        data
    );
    return result.data;
};

export const updateStoreOrder = async (
    store_id: number,
    order_id: number,
    data: IUpdateOrder
) => {
    const result: { data: IStoreOrder } = await axios.put(
        `${API}/${store_id}/orders/${order_id}`,
        data
    );
    return result.data;
};

export const createPaymentIntent = async (
    store_id: number,
    order: IStoreOrder
) => {
    const result: { data: { id: string } } = await axios.post(
        `${API}/${store_id}/orders/payment`,
        order
    );
    return result.data;
};

export const getPaymentIntent = async (
    store_id: number | string,
    payment_intent_id: string
) => {
    const result: { data: IPaymentIntent } = await axios.get(
        `${API}/${store_id}/orders/payment/${payment_intent_id}`,
        {
            headers: {
                "Cache-Control": "no-cache, no-store, must-revalidate",
                Pragma: "no-cache",
                Expires: "0",
            },
        }
    );
    return result.data;
};

export const getAllInvoices = async (
    store_id: number,
    page: number = 1,
    size: number = 50,
    filter?: IFilter
) => {
    // Get all invoices
    const result: { data: IPage<IInvoice> } = await axios.get(
        `${API}/${store_id}/invoices?page=${page}&size=${size}&${buildQuery(filter)}`,
        {
            headers: {
                "Cache-Control": "no-cache, no-store, must-revalidate",
                Pragma: "no-cache",
                Expires: "0",
            },
        }
    );
    return result.data;
};

export const getCurrentInvoiceInfo = async (store_id: number | string) => {
    const result: { data: IInvoice } = await axios.get(
        `${API}/${store_id}/invoice/`,
        {
            headers: {
                "Cache-Control": "no-cache, no-store, must-revalidate",
                Pragma: "no-cache",
                Expires: "0",
            },
        }
    );
    return result.data;
};

export const getInvoiceItems = async (store_id: number | string) => {
    const result: { data: IPage<IInvoiceItem> } = await axios.get(
        `${API}/${store_id}/invoice/items`,
        {
            headers: {
                "Cache-Control": "no-cache, no-store, must-revalidate",
                Pragma: "no-cache",
                Expires: "0",
            },
        }
    );
    return result.data;
};

export const getInvoiceItem = async (
    store_id: number | string,
    invoice_item_id: string
) => {
    const result: { data: IInvoiceItem } = await axios.get(
        `${API}/${store_id}/invoice/${invoice_item_id}`,
        {
            headers: {
                "Cache-Control": "no-cache, no-store, must-revalidate",
                Pragma: "no-cache",
                Expires: "0",
            },
        }
    );
    return result.data;
};

export const sendCurrentInvoice = async (store_id: number) => {
    const result: { data: IInvoiceItem } = await axios.post(
        `${API}/${store_id}/invoice/send`,
        {
            headers: {
                "Cache-Control": "no-cache, no-store, must-revalidate",
                Pragma: "no-cache",
                Expires: "0",
            },
        }
    );
    return result.data;
};

export const deleteCurrentInvoice = async (store_id: number) => {
    const result: { data: IInvoiceItem } = await axios.delete(
        `${API}/${store_id}/invoice`,
        {
            headers: {
                "Cache-Control": "no-cache, no-store, must-revalidate",
                Pragma: "no-cache",
                Expires: "0",
            },
        }
    );
    return result.data;
};

export const createOrder = async (store_id: number, order: IStoreOrder) => {
    const result = await axios.post<IStoreOrder>(
        `${API}/${store_id}/orders`,
        order
    );
    return result.data;
};

export const softDeleteOrder = async (store_id: number, order_id: number) => {
    const result = await axios.delete(`${API}/${store_id}/orders/${order_id}`);
    return result.data;
};

export const updateStoreHours = async (
    store_id: number | string,
    hours: IHours,
    id: number
) => {
    const result: { data: IStoreHours } = await axios.put(
        `${API}/${store_id}/hours/${id}`,
        hours
    );
    return result.data;
};

export const createStoreHours = async (
    store_id: number | string,
    hours: IHours
) => {
    const result: { data: IStoreHours } = await axios.post(
        `${API}/${store_id}/hours`,
        hours
    );
    return result.data;
};
